import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import Badge from '@/components/base/Badge';
import { useAcl } from '@/hooks/useAcl';
import type { SystemRole } from '@/hooks/useAcl';
import { getRoleHomeRoute } from '@/router/roleRoutes';

type DemoRole = 'admin' | 'therapist' | 'coordinator' | 'teacher';

const demoRoleToAclRole: Record<DemoRole, SystemRole> = {
  admin:       'Admin',
  coordinator: 'Coordinator',
  therapist:   'Therapist',
  teacher:     'Teacher',
};

// Landing route is derived from the ACL role via `getRoleHomeRoute`, never hardcoded here.
const demoRoles: { role: DemoRole; icon: string; color: string }[] = [
  { role: 'admin',       icon: 'ri-shield-check-line',  color: 'text-primary-500' },
  { role: 'coordinator', icon: 'ri-user-settings-line', color: 'text-accent-500' },
  { role: 'therapist',   icon: 'ri-stethoscope-line',   color: 'text-secondary-500' },
  { role: 'teacher',     icon: 'ri-book-open-line',     color: 'text-foreground-600' },
];

const SIGN_IN_DELAY_MS = 1200;

const features = [
  { icon: 'ri-group-line',           key: 'feature1' },
  { icon: 'ri-stethoscope-line',     key: 'feature2' },
  { icon: 'ri-calendar-schedule-line', key: 'feature3' },
  { icon: 'ri-bar-chart-grouped-line', key: 'feature4' },
];

const trustItems = [
  { icon: 'ri-shield-check-line', key: 'trustAccessible' },
  { icon: 'ri-lock-2-line',        key: 'trustEncrypted' },
  { icon: 'ri-award-line',         key: 'trustNDIS' },
];

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setRole } = useAcl();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<DemoRole>('admin');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const validate = () => {
    let valid = true;
    setEmailError('');
    setPasswordError('');
    if (!email) {
      setEmailError(t('login.emailRequired'));
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('login.emailInvalid'));
      valid = false;
    }
    if (!password) {
      setPasswordError(t('login.passwordRequired'));
      valid = false;
    } else if (password.length < 6) {
      setPasswordError(t('login.passwordMinLength'));
      valid = false;
    }
    return valid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const aclRole = demoRoleToAclRole[selectedRole];
    setTimeout(() => {
      setLoading(false);
      setRole(aclRole);
      navigate(getRoleHomeRoute(aclRole), { replace: true });
    }, SIGN_IN_DELAY_MS);
  };

  const handleReset = (e: FormEvent) => {
    e.preventDefault();
    setResetSent(true);
    setTimeout(() => {
      setForgotOpen(false);
      setResetSent(false);
      setResetEmail('');
    }, 2500);
  };

  const roleLabels: Record<DemoRole, string> = {
    admin: t('login.roleAdmin'),
    coordinator: t('login.roleCoordinator'),
    therapist: t('login.roleTherapist'),
    teacher: t('login.roleTeacher'),
  };

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* ── Left brand panel ──────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] gradient-hero relative overflow-hidden flex-col">
        {/* Glow blobs */}
        <div className="absolute top-[-80px] left-[-80px] w-72 h-72 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, oklch(0.72 0.17 58), transparent 70%)' }} />
        <div className="absolute bottom-[-60px] right-[-40px] w-64 h-64 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, oklch(0.60 0.12 215), transparent 70%)' }} />
        <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full opacity-08"
          style={{ background: 'radial-gradient(circle, oklch(0.67 0.13 108), transparent 70%)' }} />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full px-12 py-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-auto">
            <div className="w-10 h-10 flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/56cd5c38-7b0a-4a14-b4db-95b4319f943c/bf902dd9-b2ed-4d83-8680-96eb5154116a_compressed_XL_Logo.webp"
                alt={t('login.brandTitle') + ' ' + t('login.brandSubtitle')}
                className="w-9 h-9 object-contain"
              />
            </div>
            <div>
              <p className="font-heading font-700 text-foreground-50 text-lg leading-tight">{t('login.brandTitle')}</p>
              <p className="font-heading font-700 text-primary-400 text-lg leading-tight">{t('login.brandSubtitle')}</p>
            </div>
          </div>

          {/* Hero text */}
          <div className="mb-10">
            <Badge variant="primary" icon="ri-sparkling-line" className="mb-5">
              {t('login.brandTagline')}
            </Badge>
            <h1 className="font-heading font-700 text-foreground-50 text-4xl xl:text-5xl leading-tight mb-4" dangerouslySetInnerHTML={{ __html: t('login.heroTitle') }} />
            <p className="text-foreground-300 text-base font-label leading-relaxed max-w-sm">
              {t('login.heroDescription')}
            </p>
          </div>

          {/* Feature pills */}
          <div className="grid grid-cols-1 gap-3 mb-12">
            {features.map((f) => (
              <div key={f.key} className="flex items-center gap-3 glass rounded-xl px-4 py-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-50/10 flex-shrink-0">
                  <i className={`${f.icon} text-base text-primary-400`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-100 font-label leading-tight">{t(`login.${f.key}Title`)}</p>
                  <p className="text-xs text-foreground-400 font-label mt-0.5">{t(`login.${f.key}Sub`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-6 mt-auto pt-6 border-t border-foreground-50/10">
            {trustItems.map((item) => (
              <div key={item.key} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-primary-400 text-sm`} />
                <span className="text-foreground-400 text-xs font-label">{t(`login.${item.key}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right login panel ──────────────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 relative">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/56cd5c38-7b0a-4a14-b4db-95b4319f943c/bf902dd9-b2ed-4d83-8680-96eb5154116a_compressed_XL_Logo.webp"
            alt={t('login.brandTitle') + ' ' + t('login.brandSubtitle')}
            className="w-8 h-8 object-contain"
          />
          <div>
            <p className="font-heading font-700 text-foreground-900 text-base leading-tight">{t('login.brandTitle')}</p>
            <p className="font-heading font-700 text-primary-500 text-base leading-tight">{t('login.brandSubtitle')}</p>
          </div>
        </div>

        <div className="w-full max-w-md">
          {!forgotOpen ? (
            <>
              {/* Header */}
              <div className="mb-8">
                <h2 className="font-heading font-700 text-foreground-900 text-2xl mb-2">{t('login.welcomeBack')}</h2>
                <p className="text-foreground-500 text-sm font-label">{t('login.signInSubtitle')}</p>
              </div>

              {/* Role selector */}
              <div className="mb-6">
                <p className="text-sm font-medium text-foreground-700 font-label mb-2">
                  {t('login.signInAs')}
                  <span className="ml-1 text-primary-500" aria-hidden="true">*</span>
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {demoRoles.map((r) => (
                    <button
                      key={r.role}
                      type="button"
                      onClick={() => setSelectedRole(r.role)}
                      aria-pressed={selectedRole === r.role}
                      className={`
                        flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg border-2 text-center
                        transition-all duration-200 cursor-pointer
                        ${selectedRole === r.role
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-background-200 bg-background-50 hover:border-background-300 hover:bg-background-100'}
                      `}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center`}>
                        <i className={`${r.icon} text-base ${selectedRole === r.role ? 'text-primary-600' : r.color}`} />
                      </div>
                      <span className={`text-xs font-label font-medium ${selectedRole === r.role ? 'text-primary-700' : 'text-foreground-600'}`}>
                        {roleLabels[r.role]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
                <Input
                  label={t('login.emailAddress', { defaultValue: t('common.emailAddress') })}
                  type="email"
                  name="email"
                  id="login-email"
                  placeholder="you@organisation.edu.au"
                  iconLeft="ri-mail-line"
                  required
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                  error={emailError}
                  autoComplete="email"
                />
                <div>
                  <Input
                    label={t('common.password')}
                    type="password"
                    name="password"
                    id="login-password"
                    placeholder={t('login.enterPassword')}
                    iconLeft="ri-lock-2-line"
                    required
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                    error={passwordError}
                    autoComplete="current-password"
                  />
                  <div className="mt-1.5 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setForgotOpen(true)}
                      className="text-xs text-accent-600 hover:text-accent-700 font-label font-medium transition-colors cursor-pointer"
                    >
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                  iconLeft={loading ? undefined : 'ri-login-box-line'}
                >
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </Button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-background-200" />
                <span className="text-xs text-foreground-400 font-label">{t('login.demoCredentials')}</span>
                <div className="flex-1 h-px bg-background-200" />
              </div>

              {/* Demo hint */}
              <div className="bg-accent-50 border border-accent-200 rounded-xl px-4 py-3">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-accent-600 text-sm mt-0.5" />
                  <div>
                    <p className="text-xs text-accent-900 font-label font-medium">{t('login.tryDemoTitle')}</p>
                    <p className="text-xs text-accent-700 font-label mt-0.5">
                      {t('login.tryDemoDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* ── Forgot Password ── */
            <div>
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 font-label mb-6 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line" />
                {t('login.backToSignIn')}
              </button>
              <div className="mb-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mb-4">
                  <i className="ri-mail-send-line text-primary-600 text-xl" />
                </div>
                <h2 className="font-heading font-700 text-foreground-900 text-2xl mb-2">{t('login.resetPasswordTitle')}</h2>
                <p className="text-foreground-500 text-sm font-label">
                  {t('login.resetPasswordDescription')}
                </p>
              </div>
              {!resetSent ? (
                <form onSubmit={handleReset} className="flex flex-col gap-5">
                  <Input
                    label={t('common.emailAddress')}
                    type="email"
                    name="reset_email"
                    id="reset-email"
                    placeholder="you@organisation.edu.au"
                    iconLeft="ri-mail-line"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <Button type="submit" variant="primary" size="lg" fullWidth iconLeft="ri-send-plane-line">
                    {t('login.sendResetLink')}
                  </Button>
                </form>
              ) : (
                <div className="bg-semantic-success-bg border border-semantic-success/30 rounded-xl px-5 py-5 text-center">
                  <i className="ri-checkbox-circle-line text-semantic-success text-2xl mb-2" />
                  <p className="font-heading font-700 text-foreground-800 text-sm">{t('login.emailSentTitle')}</p>
                  <p className="text-xs text-foreground-500 font-label mt-1">{t('login.emailSentDescription')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="absolute bottom-6 text-xs text-foreground-400 font-label text-center px-4">
          &copy; {new Date().getFullYear()} {t('login.brandTitle')} {t('login.brandSubtitle')} &middot; {t('login.privacyPolicy')} &middot; {t('login.termsOfService')}
        </p>
      </div>
    </div>
  );
}